/**
 * Skill Modal Component
 * Modal for creating and editing skills - matches app design system
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLanguage } from '../../../hooks/useLanguage';
import { useFeature } from '@/hooks/useFeature';
import { message } from 'antd';
import { UploadArea, AgentPreview, ConfigGuideModal, GuideInfoButton, ExpandableTextarea, SplitPreviewModal, FilePreviewModal } from '../../../components/common';
import { ContextPromptViewer } from '../../../components/common/ContextPromptViewer';
import { SkillsApi } from '../services/skillsApi';
import api from '@/services/api';
import { DataBundleApi } from '../../data-bundles/services/dataBundleService';
import type { DataBundle } from '../../data-bundles/types';
// Direct path, not the module barrel: the barrel re-exports VaultPage, which
// would drag the whole Vault workspace into this chunk.
import { VaultAttachments } from '../../vault/components/VaultAttachments';
import type { Skill, SkillFile, SkillCategory, CreateSkillRequest, UpdateSkillRequest } from '../types';
import { KNOWLEDGE_ACCEPT } from '@/constants/knowledgeFileTypes';
import './SkillModal.css';

interface SkillModalProps {
  visible: boolean;
  skill?: Skill;
  categories?: SkillCategory[];
  onSave: () => void;
  onCancel: () => void;
}

export const SkillModal: React.FC<SkillModalProps> = ({
  visible,
  skill,
  categories: _categories,
  onSave,
  onCancel,
}) => {
  const { t } = useLanguage();
  // Library on => the legacy Data Bundles picker retires from this editor;
  // knowledge comes from the Library section (VaultAttachments) instead.
  const libraryEnabled = useFeature('modules.library');
  const [loading, setLoading] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [skillTokenInfo, setSkillTokenInfo] = useState<{ tokens: number; budget: number; over: boolean } | null>(null);
  const [skillPreview, setSkillPreview] = useState<string>('');
  const [loadingPreview, setLoadingPreview] = useState(false);
  // Removed generatedPreview state — auto-file-sync replaces the
  // preview-then-apply flow for the files context prompt. The
  // activation-prompt preview below is a separate feature.
  const [generatingActivation, setGeneratingActivation] = useState(false);
  const [activationPreview, setActivationPreview] = useState<string | null>(null);
  const [regeneratingFilesContext, setRegeneratingFilesContext] = useState(false);
  const [formData, setFormData] = useState({
    skill_name: '',
    skill_description: '',
    skill_category: '',
    skill_system_prompt: '',
    activation_prompt: '',
    files_context_prompt: '',
  });
  const [triggers, setTriggers] = useState<string[]>([]);
  const [triggerInput, setTriggerInput] = useState('');
  const [skillFiles, setSkillFiles] = useState<SkillFile[]>([]);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [togglingIds, setTogglingIds] = useState<Set<string>>(new Set());
  const [previewFile, setPreviewFile] = useState<{ fileId?: string; fileName: string; fileType: string } | null>(null);
  const [dataBundles, setDataBundles] = useState<DataBundle[]>([]);
  const [selectedBundleIds, setSelectedBundleIds] = useState<string[]>([]);
  const [showBundleSelector, setShowBundleSelector] = useState(false);
  const [bundleSearchQuery, setBundleSearchQuery] = useState('');
  const [bundleContextMap, setBundleContextMap] = useState<Record<string, boolean>>({});
  const [togglingBundleIds, setTogglingBundleIds] = useState<Set<string>>(new Set());
  const [bundleError, setBundleError] = useState<string | null>(null);

  useEffect(() => {
    if (visible && skill) {
      // Edit mode - fetch full skill data (list doesn't include config/files)
      setFormData({
        skill_name: skill.skill_name,
        skill_description: skill.skill_description || '',
        skill_category: skill.skill_category || '',
        skill_system_prompt: '',
        activation_prompt: '',
        files_context_prompt: '',
      });
      setTriggers([]);
      setSkillFiles([]);
      setSelectedBundleIds(skill.data_bundle_ids || []);
      // Fetch full skill (with config) and files in parallel
      Promise.all([
        SkillsApi.getSkill(skill.skill_id),
        SkillsApi.getSkillFiles(skill.skill_id),
      ]).then(([fullSkill, files]) => {
        setFormData({
          skill_name: fullSkill.skill_name,
          skill_description: fullSkill.skill_description || '',
          skill_category: fullSkill.skill_category || '',
          skill_system_prompt: fullSkill.skill_system_prompt,
          activation_prompt: fullSkill.activation_prompt || '',
          files_context_prompt: fullSkill.files_context_prompt || '',
        });
        setTriggers(fullSkill.skill_triggers || []);
        setSkillFiles(files);
        setSelectedBundleIds(fullSkill.data_bundle_ids || []);
        // Load bundle context flags from enriched data_bundles array
        if (fullSkill.data_bundles) {
          const contextMap: Record<string, boolean> = {};
          for (const b of fullSkill.data_bundles) {
            contextMap[b.data_bundle_id] = b.include_as_context || false;
          }
          setBundleContextMap(contextMap);
        }
        // Load sub-agent instruction preview
        setLoadingPreview(true);
        api.get(`/skills/${skill.skill_id}/instruction-preview`)
          .then(res => {
            setSkillPreview(res.data.instruction || '');
          })
          .catch(err => {
            console.error('[SkillModal] Failed to load instruction preview:', err?.response?.data || err);
            setSkillPreview(`Failed to load preview: ${err?.response?.data?.error || err?.message || t('common.unknownError')}`);
          })
          .finally(() => setLoadingPreview(false));
      }).catch(err => console.error('Error loading skill details:', err));
    } else if (visible) {
      // Create mode - reset form
      setFormData({
        skill_name: '',
        skill_description: '',
        skill_category: '',
        skill_system_prompt: '',
        activation_prompt: '',
        files_context_prompt: '',
      });
      setTriggers([]);
      setSkillFiles([]);
      setSelectedBundleIds([]);
      setBundleContextMap({});
    }

    // Load data bundles when modal opens
    if (visible) {
      DataBundleApi.listAvailableBundles()
        .then(data => setDataBundles(data.data_bundles))
        .catch(err => console.error('Error loading data bundles:', err));
    }
  }, [visible, skill]);

  // Client-side token estimate for skill content
  useEffect(() => {
    if (!skill) { setSkillTokenInfo(null); return; }
    const totalChars = (formData.skill_system_prompt?.length || 0)
      + (formData.activation_prompt?.length || 0)
      + (formData.files_context_prompt?.length || 0);
    const tokens = Math.ceil(totalChars / 4);
    const budget = 80000; // default standard tier skill budget
    setSkillTokenInfo({ tokens, budget, over: tokens > budget });
  }, [skill, formData.skill_system_prompt, formData.activation_prompt, formData.files_context_prompt]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.skill_name.trim() || !formData.skill_system_prompt.trim()) {
      alert(t('skills.fillRequiredFields'));
      return;
    }

    setLoading(true);
    try {
      const data = {
        ...formData,
        skill_triggers: triggers,
        is_active: true,
        data_bundle_ids: selectedBundleIds.length > 0 ? selectedBundleIds : [],
      };

      if (skill) {
        await SkillsApi.updateSkill(skill.skill_id, data as UpdateSkillRequest);
      } else {
        await SkillsApi.createSkill(data as CreateSkillRequest);
      }

      onSave();
    } catch (error: any) {
      console.error('Error saving skill:', error);
      alert(skill ? t('skills.updateFailedShort') : t('skills.createFailedShort'));
    } finally {
      setLoading(false);
    }
  };

  const handleAddTrigger = () => {
    if (triggerInput && !triggers.includes(triggerInput)) {
      setTriggers([...triggers, triggerInput]);
      setTriggerInput('');
    }
  };

  const handleRemoveTrigger = (trigger: string) => {
    setTriggers(triggers.filter(t => t !== trigger));
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTrigger();
    }
  };

  // After upload/delete the cascade regenerates the skill's
  // files_context_prompt on a debounced timer. Poll the server six
  // times across the realistic regen window (3s, 5s, 8s, 12s, 18s,
  // 25s). The first poll that returns the regenerated value updates
  // the inline ContextPromptViewer — no need to close + reopen the
  // modal.
  const scheduleSkillContextRefresh = useCallback(() => {
    if (!skill) return;
    const refresh = async () => {
      try {
        const fresh = await SkillsApi.getSkill(skill.skill_id);
        setFormData(prev => ({
          ...prev,
          files_context_prompt: fresh.files_context_prompt || '',
        }));
        const bundlesResp = await DataBundleApi.listAvailableBundles();
        setDataBundles(bundlesResp.data_bundles || []);
      } catch (err) {
        console.error('[SkillModal] context refresh failed:', err);
      }
    };
    [3_000, 5_000, 8_000, 12_000, 18_000, 25_000].forEach(ms =>
      setTimeout(refresh, ms),
    );
  }, [skill]);

  // File upload handlers
  const handleFileUpload = useCallback(async (files: FileList | File[]) => {
    if (!skill) return; // Can only upload files in edit mode (skill must exist)

    setUploading(true);
    try {
      for (const file of Array.from(files)) {
        const attachment = await SkillsApi.uploadSkillFile(skill.skill_id, file);
        setSkillFiles(prev => [...prev, attachment]);
      }
      scheduleSkillContextRefresh();
    } catch (error: any) {
      console.error('Error uploading file:', error);
      const msg = error?.response?.data?.error || t('skills.uploadFailed');
      alert(msg);
    } finally {
      setUploading(false);
    }
  }, [skill, scheduleSkillContextRefresh]);

  const handleFileDelete = async (attachmentId: string) => {
    if (!skill) return;

    try {
      await SkillsApi.deleteSkillFile(skill.skill_id, attachmentId);
      setSkillFiles(prev => prev.filter(f => f.skill_attachment_id !== attachmentId));
      scheduleSkillContextRefresh();
    } catch (error: any) {
      console.error('Error deleting file:', error);
      alert(t('skills.deleteFileFailed'));
    }
  };

  const CONTEXT_ALLOWED_EXTENSIONS = ['.txt', '.md', '.csv', '.json', '.doc', '.docx', '.pdf', '.pages'];

  const isContextAllowed = (fileType: string, fileName: string): boolean => {
    const ext = '.' + (fileName || '').split('.').pop()?.toLowerCase();
    const mime = (fileType || '').toLowerCase();
    if (mime.startsWith('image/') || mime.startsWith('video/') || mime.startsWith('audio/')) return false;
    return CONTEXT_ALLOWED_EXTENSIONS.includes(ext);
  };

  const handleToggleContext = async (file: SkillFile) => {
    if (!skill) return;
    const newValue = !file.include_as_context;
    const id = file.skill_attachment_id;

    setTogglingIds(prev => new Set(prev).add(id));
    try {
      const updated = await SkillsApi.updateSkillFile(skill.skill_id, id, { include_as_context: newValue });
      setSkillFiles(prev => prev.map(f => f.skill_attachment_id === id ? { ...f, include_as_context: updated.include_as_context } : f));
    } catch (err: any) {
      const msg = err?.response?.data?.error || t('skills.contextUpdateFailed');
      alert(msg);
    } finally {
      setTogglingIds(prev => { const s = new Set(prev); s.delete(id); return s; });
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files.length > 0) {
      handleFileUpload(e.dataTransfer.files);
    }
  };

  const selectedBundles = useMemo(
    () => dataBundles.filter(b => selectedBundleIds.includes(b.data_bundle_id)),
    [dataBundles, selectedBundleIds]
  );

  const toggleBundleSelection = useCallback((bundleId: string) => {
    setSelectedBundleIds(prev =>
      prev.includes(bundleId)
        ? prev.filter(id => id !== bundleId)
        : [...prev, bundleId]
    );
  }, []);

  const handleToggleBundleContext = async (bundleId: string) => {
    if (!skill) return;
    const newValue = !bundleContextMap[bundleId];
    setTogglingBundleIds(prev => new Set(prev).add(bundleId));
    setBundleError(null);
    try {
      await SkillsApi.updateSkillDataBundle(skill.skill_id, bundleId, { include_as_context: newValue });
      setBundleContextMap(prev => ({ ...prev, [bundleId]: newValue }));
    } catch (err: any) {
      const msg = err?.response?.data?.error || t('skills.contextToggleFailed');
      setBundleError(msg);
      setTimeout(() => setBundleError(null), 6000);
    } finally {
      setTogglingBundleIds(prev => { const s = new Set(prev); s.delete(bundleId); return s; });
    }
  };

  const filteredBundles = useMemo(() => {
    if (!bundleSearchQuery) return dataBundles;
    const query = bundleSearchQuery.toLowerCase();
    return dataBundles.filter(b =>
      b.bundle_name.toLowerCase().includes(query) ||
      (b.bundle_description || '').toLowerCase().includes(query) ||
      (b.created_by_name || '').toLowerCase().includes(query)
    );
  }, [dataBundles, bundleSearchQuery]);

  const getFileIcon = (fileType: string) => {
    if (fileType?.startsWith('image/')) return 'image';
    if (fileType?.includes('pdf')) return 'pdf';
    if (fileType?.includes('spreadsheet') || fileType?.includes('excel')) return 'sheet';
    return 'doc';
  };

  if (!visible) return null;

  const isEditMode = !!skill;

  return (
    <div className="modal-overlay">
      <div dir="auto" className="modal-container skill-modal" onClick={(e) => e.stopPropagation()}>
        {/* Modal Header */}
        <div className="modal-header">
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <h2 className="modal-title">
                {skill ? t('skills.editSkillTitle') : t('skills.createSkillTitle')}
              </h2>
              <GuideInfoButton onClick={() => setShowGuide(true)} />
            </div>
            <p className="modal-subtitle">{t('skills.defineBehavior')}</p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', marginInlineStart: 'auto', gap: '8px' }}>
            {skill && (
              <button
                type="button"
                style={{
                  background: 'none',
                  border: '1px solid var(--border-color, #d0d5dd)',
                  borderRadius: '5px',
                  padding: '4px 8px',
                  cursor: regeneratingFilesContext ? 'wait' : 'pointer',
                  color: 'var(--text-secondary, #667085)',
                  display: 'flex',
                  alignItems: 'center',
                  opacity: regeneratingFilesContext ? 0.5 : 1,
                }}
                disabled={regeneratingFilesContext}
                title={t('skills.reSummarize')}
                onClick={async () => {
                  try {
                    setRegeneratingFilesContext(true);
                    await api.post(`/skills/${skill.skill_id}/generate-files-context`);
                    const fresh = await SkillsApi.getSkill(skill.skill_id);
                    setFormData(prev => ({
                      ...prev,
                      files_context_prompt: fresh.files_context_prompt || '',
                    }));
                  } catch (err) {
                    console.error('[SkillModal] regenerate files context failed', err);
                  } finally {
                    setRegeneratingFilesContext(false);
                  }
                }}
              >
                <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" className={regeneratingFilesContext ? 'spinning' : ''}>
                  <path d="M1 4v6h6" /><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
                </svg>
              </button>
            )}
            <button
              type="button"
              className="modal-close"
              onClick={onCancel}
              aria-label={t('common.close')}
            >
              <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>
        </div>

        {/* Modal Body */}
        <form onSubmit={handleSubmit} className="modal-body">
          {/* Skill Name */}
          <div className="form-group">
            <label className="form-label">
              {t('skills.nameLabel')} <span className="required">*</span>
            </label>
            <input dir="auto"
              type="text"
              className="form-input"
              placeholder={t('skills.namePlaceholderExample')}
              value={formData.skill_name}
              onChange={(e) => setFormData({ ...formData, skill_name: e.target.value })}
              disabled={loading}
            />
          </div>

          {/* Description */}
          <div className="form-group">
            <label className="form-label">{t('skills.description')}</label>
            <input dir="auto"
              type="text"
              className="form-input"
              placeholder={t('skills.descriptionPlaceholderExample')}
              value={formData.skill_description}
              onChange={(e) => setFormData({ ...formData, skill_description: e.target.value })}
              disabled={loading}
            />
          </div>

          {/* When to Activate */}
          <div className="form-group">
            <div className="section-header-row">
              <div>
                <label className="section-label">
                  {t('skills.whenToActivateLabel')} <span className="optional">{t('common.recommendedSuffix')}</span>
                </label>
                <p className="section-description">
                  {t('skills.describeWhenTheAgentShould')}
                </p>
              </div>
              {skill && (
                <button
                  type="button"
                  className={`btn-generate-context ${generatingActivation ? 'generating' : ''}`}
                  title={t('skills.suggestTriggersTip')}
                  onClick={async () => {
                    setGeneratingActivation(true);
                    try {
                      const res = await api.post(`/skills/${skill.skill_id}/generate-activation-prompt`, {}, { timeout: 60000 });
                      const prompt = res.data.activation_prompt || '';
                      if (prompt) {
                        setActivationPreview(prompt);
                      } else {
                        message.info(t('skills.noPromptGenerated'));
                      }
                    } catch (error: any) {
                      message.error(error?.response?.data?.error || t('skills.generateActivationFailed'));
                    } finally {
                      setGeneratingActivation(false);
                    }
                  }}
                  disabled={generatingActivation || !formData.skill_name.trim()}
                >
                  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1 4v6h6" />
                    <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
                  </svg>
                  {generatingActivation ? 'Generating...' : formData.activation_prompt ? t('common.regenerate') : t('common.generate')}
                </button>
              )}
            </div>
            <ExpandableTextarea
              value={formData.activation_prompt}
              onChange={(val) => setFormData({ ...formData, activation_prompt: val })}
              placeholder=""
              disabled={loading || generatingActivation}
              rows={8}
              label={t('skills.whenToActivate')}
            />
          </div>

          {/* Skill Prompt */}
          <div className="form-group">
            <label className="form-label">
              {t('skills.skillPromptLabel')} <span className="required">*</span>
            </label>
            <ExpandableTextarea
              value={formData.skill_system_prompt}
              onChange={(val) => setFormData({ ...formData, skill_system_prompt: val })}
              placeholder=""
              disabled={loading}
              rows={6}
              label={t('skills.skillPrompt')}
            />
          </div>

          {/* Skill Files Section */}
          <div className="data-bundle-section">
            <label className="section-label">{t('skills.skillFiles')}</label>
            <p className="section-description">
              {t('skills.attachKnowledgeFilesToProvide')}
            </p>

            {/* File List */}
            {skillFiles.length > 0 && (
              <div className="skill-files-list">
                {skillFiles.map(file => (
                  <div key={file.skill_attachment_id} className="skill-file-item">
                    <div
                      className="skill-file-clickable"
                      onClick={() => setPreviewFile({
                        fileId: file.file_id,
                        fileName: file.attachment_filename || file.file_name,
                        fileType: file.file_type || '',
                      })}
                      style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1, minWidth: 0, cursor: 'pointer' }}
                    >
                    <div className="skill-file-icon">
                      <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
                        {getFileIcon(file.file_type) === 'pdf' ? (
                          <><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /></>
                        ) : getFileIcon(file.file_type) === 'image' ? (
                          <><rect x="3" y="3" width="18" height="18" rx="2" ry="2" /><circle cx="8.5" cy="8.5" r="1.5" /><polyline points="21 15 16 10 5 21" /></>
                        ) : (
                          <><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /></>
                        )}
                      </svg>
                    </div>
                    <div className="skill-file-info">
                      <span className="skill-file-name" dir="auto">{file.attachment_filename || file.file_name}</span>
                      {file.file_size_mb != null && (
                        <span className="skill-file-size">{file.file_size_mb.toFixed(2)} MB</span>
                      )}
                    </div>
                    </div>
                    <div className="skill-file-context-toggle" title={
                      isContextAllowed(file.file_type, file.file_name || file.attachment_filename)
                        ? t('skills.alwaysInContextTooltip')
                        : t('skills.contextToggleUnsupportedType')
                    }>
                      <span className="context-toggle-label">{t('skills.alwaysInContext')}</span>
                      <label className="context-switch">
                        <input
                          type="checkbox"
                          checked={file.include_as_context || false}
                          onChange={() => handleToggleContext(file)}
                          disabled={
                            !isContextAllowed(file.file_type, file.file_name || file.attachment_filename) ||
                            togglingIds.has(file.skill_attachment_id)
                          }
                        />
                        <span className="context-switch-slider"></span>
                      </label>
                    </div>
                    <button
                      type="button"
                      className="skill-file-delete"
                      onClick={() => handleFileDelete(file.skill_attachment_id)}
                      title={t('skills.removeFile')}
                    >
                      <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="18" y1="6" x2="6" y2="18"></line>
                        <line x1="6" y1="6" x2="18" y2="18"></line>
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Upload Area */}
            {isEditMode ? (
              <UploadArea
                uploading={uploading}
                dragOver={dragOver}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onFileSelect={(files) => handleFileUpload(files)}
                accept={KNOWLEDGE_ACCEPT}
              />
            ) : (
              <div className="placeholder-area">
                <p>{t('skills.saveSkillFirstForFiles')}</p>
              </div>
            )}
          </div>

          {/* The Vault as a third source, beside uploads and data bundles.
              Edit mode only — a link needs a skill_id to point at. Hidden
              where the tenant has no Library: the API refuses those calls. */}
          {libraryEnabled && isEditMode && skill && (
            <VaultAttachments subject={{ skill_id: skill.skill_id }} />
          )}

          {/* Data Bundles Selector — retired once the tenant is on the
              Library (the section above covers the same ground). */}
          {!libraryEnabled && (
          <div className="form-group">
            <label className="section-label">
              {t('skills.dataBundlesLabel')} <span className="optional">{t('common.optionalSuffix')}</span>
            </label>
            <p className="section-description">
              {t('skills.attachDataBundlesToProvide')}
            </p>

            {selectedBundles.length > 0 && (
              <div className="bundle-selected-list">
                {selectedBundles.map(bundle => (
                  <div key={bundle.data_bundle_id} className="bundle-selected-card">
                    <div className="bundle-selected-icon">
                      <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
                      </svg>
                    </div>
                    <div className="bundle-selected-info">
                      <div className="bundle-selected-name" dir="auto">{bundle.bundle_name}</div>
                      <div className="bundle-selected-meta">
                        {t('counts.files', { count: bundle.file_count })}
                        {bundle.created_by_name && (
                          <> &middot; by {bundle.created_by_name}</>
                        )}
                      </div>
                    </div>
                    {skill && (
                      <div className="bundle-context-toggle" title={t('skills.bundleAlwaysInContextTooltip')}>
                        <span className="context-toggle-label">{t('skills.bundleAlwaysInContext')}</span>
                        <label className="context-switch" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={bundleContextMap[bundle.data_bundle_id] || false}
                            onChange={() => handleToggleBundleContext(bundle.data_bundle_id)}
                            disabled={togglingBundleIds.has(bundle.data_bundle_id)}
                          />
                          <span className="context-switch-slider"></span>
                        </label>
                      </div>
                    )}
                    <button
                      type="button"
                      className="bundle-remove-btn"
                      onClick={() => toggleBundleSelection(bundle.data_bundle_id)}
                      disabled={loading}
                      title={t('bots.modal.removeDataBundle')}
                    >
                      <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="18" y1="6" x2="6" y2="18"></line>
                        <line x1="6" y1="6" x2="18" y2="18"></line>
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            )}

            {bundleError && (
              <div className="context-files-error">{bundleError}</div>
            )}

            <button
              type="button"
              className="bundle-select-btn"
              onClick={() => setShowBundleSelector(true)}
              disabled={loading}
            >
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="12" y1="5" x2="12" y2="19"></line>
                <line x1="5" y1="12" x2="19" y2="12"></line>
              </svg>
              {selectedBundles.length > 0 ? t('bots.addRemoveBundles') : t('bots.modal.selectDataBundles')}
            </button>
          </div>
          )}

          {/* Skill Knowledge Context — auto-syncs on every file change.
              Read-only. ONE viewer showing the skill's own column,
              built by the same combined-generation logic the legacy
              "Generate" button used: bundles' prompts verbatim under
              `### <Bundle Name>` headers + a `### Direct Files`
              section. Same shape as before any of these changes. */}
          {skill && (
            <ContextPromptViewer
              prompt={formData.files_context_prompt || ''}
              label={t('skills.knowledgeContext')}
              defaultOpen
            />
          )}

          {/* Trigger Keywords */}
          <div className="form-group">
            <label className="section-label">
              {t('skills.triggerKeywordsLabel')} <span className="optional">{t('common.optionalSuffix')}</span>
            </label>
            <p className="section-description">
              {t('skills.shortKeywordsThatHelpThe')}
            </p>

            <div className="trigger-input-group">
              <input dir="auto"
                type="text"
                className="form-input"
                placeholder={t('skills.addKeyword')}
                value={triggerInput}
                onChange={(e) => setTriggerInput(e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={loading}
              />
              <button
                type="button"
                className="btn-add-trigger"
                onClick={handleAddTrigger}
                disabled={loading || !triggerInput}
              >
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="12" y1="5" x2="12" y2="19"></line>
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                </svg>
              </button>
            </div>

            {triggers.length > 0 && (
              <div className="triggers-list">
                {triggers.map(trigger => (
                  <span key={trigger} className="trigger-tag">
                    {trigger}
                    <button
                      type="button"
                      className="trigger-remove"
                      onClick={() => handleRemoveTrigger(trigger)}
                      disabled={loading}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
          {/* Token Budget Bar */}
          {skill && skillTokenInfo && (
            <div style={{
              marginBottom: '12px',
              padding: '10px 14px',
              borderRadius: '8px',
              background: skillTokenInfo.over ? 'rgba(239, 68, 68, 0.08)' : 'rgba(59, 130, 246, 0.06)',
              border: `1px solid ${skillTokenInfo.over ? 'rgba(239, 68, 68, 0.2)' : 'rgba(59, 130, 246, 0.15)'}`,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                <span style={{ fontSize: '12px', fontWeight: 500, color: skillTokenInfo.over ? '#ef4444' : 'var(--text-secondary, #667085)' }}>
                  {t('skills.skillContextBudget', { used: skillTokenInfo.tokens.toLocaleString(), budget: skillTokenInfo.budget.toLocaleString() })}
                </span>
                <span style={{ fontSize: '12px', fontWeight: 600, color: skillTokenInfo.over ? '#ef4444' : '#3b82f6' }}>
                  {Math.round((skillTokenInfo.tokens / skillTokenInfo.budget) * 100)}%
                </span>
              </div>
              <div style={{ height: '4px', borderRadius: '2px', background: 'var(--border-color, #e5e7eb)', overflow: 'hidden' }}>
                <div style={{
                  height: '100%',
                  borderRadius: '2px',
                  width: `${Math.min(100, (skillTokenInfo.tokens / skillTokenInfo.budget) * 100)}%`,
                  background: skillTokenInfo.over ? '#ef4444' : skillTokenInfo.tokens / skillTokenInfo.budget > 0.8 ? '#f59e0b' : '#3b82f6',
                  transition: 'width 0.3s ease',
                }} />
              </div>
              {skillTokenInfo.over && (
                <p style={{ margin: '6px 0 0', fontSize: '11px', color: '#ef4444' }}>
                  {t('skills.overBudgetReduceSkillPrompt')}
                </p>
              )}
            </div>
          )}
          {/* Sub-Agent Instruction Preview */}
          {skill && (
            <AgentPreview
              title={t('skills.subAgentInstructionPreview')}
              subtitle={t('skills.promptPreviewSubtitle')}
              content={loadingPreview ? t('skills.loadingInstruction') : (skillPreview || t('skills.saveFirstForPreview'))}
              refreshing={loadingPreview}
              onRefresh={async () => {
                // Rebuild this skill's combined prompt: copy each linked
                // bundle's existing bundle_context_prompt verbatim, and
                // Haiku-summarize each of THIS skill's direct files.
                // Bundles are NOT re-Haiku'd from here — the auto-file-sync
                // cascade keeps them current when their files change, and
                // pressing refresh on the bundle itself is the explicit
                // "force re-summarize" action.
                try {
                  setLoadingPreview(true);
                  try {
                    await api.post(`/skills/${skill.skill_id}/generate-files-context`);
                  } catch (e: any) {
                    if (e?.response?.status !== 403) throw e;
                  }
                  const res = await api.get(`/skills/${skill.skill_id}/instruction-preview`);
                  setSkillPreview(res.data.instruction || '');
                  try {
                    const fresh = await SkillsApi.getSkill(skill.skill_id);
                    if (fresh && fresh.files_context_prompt !== undefined) {
                      setFormData(prev => ({
                        ...prev,
                        files_context_prompt: fresh.files_context_prompt || '',
                      }));
                    }
                  } catch {
                    /* non-fatal: viewer keeps prior content */
                  }
                } catch (err) {
                  console.error('[SkillModal] refresh failed', err);
                } finally {
                  setLoadingPreview(false);
                }
              }}
            />
          )}
        </form>

        {/* Modal Footer */}
        <div className="modal-actions">
          <span style={{ flex: 1 }} />
          <button
            type="button"
            className="btn-cancel"
            onClick={onCancel}
            disabled={loading}
          >
            {t('vault.cancel')}
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? t('common.saving') : t('skills.saveSkill')}
          </button>
        </div>
      </div>

      {/* Bundle Selector Modal (Multi-Select) */}
      {showBundleSelector && (
        <div className="modal-overlay bundle-selector-overlay">
          <div className="bundle-selector-modal" onClick={(e) => e.stopPropagation()}>
            <div className="bundle-selector-header">
              <h2>{t('bots.modal.selectDataBundles')}</h2>
              {selectedBundleIds.length > 0 && (
                <span className="bundle-selector-count">{t('common.nSelected', { count: selectedBundleIds.length })}</span>
              )}
              <button
                type="button"
                className="modal-close"
                onClick={() => { setShowBundleSelector(false); setBundleSearchQuery(''); }}
              >
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>

            <div className="bundle-search-container">
              <svg className="bundle-search-icon" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"></circle>
                <path d="m21 21-4.35-4.35"></path>
              </svg>
              <input dir="auto"
                type="text"
                className="bundle-search-input"
                placeholder={t('bots.modal.searchBundlesPlaceholder')}
                value={bundleSearchQuery}
                onChange={(e) => setBundleSearchQuery(e.target.value)}
                autoFocus
              />
              {bundleSearchQuery && (
                <button
                  type="button"
                  className="bundle-search-clear"
                  onClick={() => setBundleSearchQuery('')}
                >
                  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
              )}
            </div>

            <div className="bundle-selector-body">
              {filteredBundles.length === 0 && bundleSearchQuery ? (
                <div className="bundle-empty-state">
                  <div className="bundle-empty-text">No bundles match "{bundleSearchQuery}"</div>
                </div>
              ) : (
                filteredBundles.map((bundle) => {
                  const isSelected = selectedBundleIds.includes(bundle.data_bundle_id);
                  return (
                    <div
                      key={bundle.data_bundle_id}
                      className={`bundle-list-item ${isSelected ? 'selected' : ''}`}
                      onClick={() => toggleBundleSelection(bundle.data_bundle_id)}
                    >
                      <div className={`bundle-item-checkbox ${isSelected ? 'checked' : ''}`}>
                        {isSelected && (
                          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="3">
                            <polyline points="20 6 9 17 4 12"></polyline>
                          </svg>
                        )}
                      </div>
                      <div className="bundle-item-icon">
                        <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
                        </svg>
                      </div>
                      <div className="bundle-item-content">
                        <div className="bundle-item-name" dir="auto">{bundle.bundle_name}</div>
                        {bundle.bundle_description && (
                          <div className="bundle-item-description" dir="auto">{bundle.bundle_description}</div>
                        )}
                        <div className="bundle-item-meta">
                          <span>{t('counts.files', { count: bundle.file_count })}</span>
                          {bundle.created_by_name && (
                            <span>by {bundle.created_by_name}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            <div className="bundle-selector-footer">
              {selectedBundleIds.length > 0 && (
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={() => setSelectedBundleIds([])}
                >
                  {t('bots.clearAll')}
                </button>
              )}
              <button
                type="button"
                className="btn-primary"
                onClick={() => { setShowBundleSelector(false); setBundleSearchQuery(''); }}
              >
                {t('common.done')}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfigGuideModal section="skill" visible={showGuide} onClose={() => setShowGuide(false)} />

      {/* Generated Activation Prompt Preview Modal */}
      {activationPreview !== null && (
        <SplitPreviewModal
          title={t('skills.generatedActivationPrompt')}
          currentValue={formData.activation_prompt}
          generatedValue={activationPreview}
          onGeneratedChange={setActivationPreview}
          onApply={() => {
            setFormData(prev => ({ ...prev, activation_prompt: activationPreview! }));
            setActivationPreview(null);
            message.success(t('skills.promptApplied'));
          }}
          onCancel={() => setActivationPreview(null)}
        />
      )}

      <FilePreviewModal
        visible={!!previewFile}
        fileId={previewFile?.fileId}
        fileName={previewFile?.fileName || ''}
        fileType={previewFile?.fileType}
        onClose={() => setPreviewFile(null)}
      />
    </div>
  );
};
